import { MachineDetailTabsProvider } from "../../../../machines/shared/components/views/machines/detail/tabs";
import { Machine } from "../../../../machines/shared/models/machine";
import { MachineConfiguration } from "../../../../machines/shared/models/machineConfiguration";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../../records/grids/columns/columnDefinitionSetService";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../../../records/grids/settings/gridSettingsService";
import {
  ConfigurableGridFormViewComponentController,
  vsComponentName as configurableGridFormViewComponentName,
  ConfigurableGridFormViewConfig,
  ConfigurableGridFormViewControllerHandler,
  ToggleButtonDefinition,
} from "../../../../records/views/configurableGridFormView";
import {
  GridConfiguration,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../../../../records/views/recordGridView";
import { IQuery, ServiceResponse } from "../../../../records/server/query";
import { ColDef } from "../../../../records/grids/columns/colDef";
import {
  vsObjectName as contractMachineName,
  vsContractColumnSetName as contractColumnSetName,
} from "../contractMachines";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import { Record } from "../../../../records/server/crud";
import {
  ConfigurableGridFormViewService,
  vsServiceName as ConfigurableGridFormViewServiceName,
} from "../../../../records/views/configurableGridFormView/service";
import { GridSelectionChangedArgs } from "../../../../records/grids/controller";
import { machineObjectName } from "../../../../machines/shared/names";
export * from "./contractAmendmentTab";

export function addContractTabToMachineDetail(config: {
  machineDetailsTabsProvider: MachineDetailTabsProvider;
  displayName: string;
  id: string;
  uiSrefFragment: string;
  contractTypeId: string;
  gridEditSymbolKey: string;
  gridViewSymbolKey: string;
}) {
  const {
    machineDetailsTabsProvider,
    displayName,
    id,
    uiSrefFragment,
    contractTypeId,
    gridEditSymbolKey,
    gridViewSymbolKey,
  } = config;

  machineDetailsTabsProvider.addElement({
    displayName,
    id,
    uiSrefFragment,
    requiredPermission: "contractView",
    stateDefinition: {
      url: `/${uiSrefFragment}`,
      component: configurableGridFormViewComponentName,
      resolve: {
        defaultQuery: [
          "machine",
          (machine: any) => ({ MachineId: machine.Id }),
        ],
        config: [
          RecordGridViewServiceName,
          GridSettingsServiceName,
          "machine",
          ColumnDefinitionSetServiceName,
          ColumnDefinitionServiceName,
          (
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
            machine: Machine<MachineConfiguration>,
            columnDefinitionSetService: ColumnDefinitionSetService,
            columnDefinitionService: ColumnDefinitionService,
          ): ConfigurableGridFormViewConfig =>
            getConfigurableGridFormViewConfigForContractMachines(
              recordGridViewService,
              gridSettingsService.getGridSettings(Symbol(gridViewSymbolKey)),
              { MachineId: machine.Id },
              columnDefinitionService,
              columnDefinitionSetService,
            ),
        ],
      },
    },
    children: [
      {
        displayName: "Edit Contracts",
        id: "edit",
        requiredPermission: "contractEdit",
        uiSrefFragment: "edit",
        stateDefinition: {
          url: "/edit",
          views: {
            "@^.^": {
              component: configurableGridFormViewComponentName,
            },
          },
          resolve: {
            selectedIds: [
              ServerUtilityName,
              RecordGridViewServiceName,
              "machine",
              (
                serverUtility: ServerUtility,
                recordGridViewService: RecordGridViewService,
                machine: Record,
              ) => {
                const gridConfig =
                  recordGridViewService.getGridConfiguration(
                    contractMachineName,
                  )!;
                return serverUtility
                  .fetchAll<Record>({
                    url: gridConfig.gridDataSource!.url,
                    query: { MachineId: machine.Id },
                  })
                  .then((result) => result.map((r) => r.ContractInstanceId));
              },
            ],
            gridConfiguration: [
              RecordGridViewServiceName,
              GridSettingsServiceName,
              (
                recordGridViewService: RecordGridViewService,
                gridSettingsService: GridSettingsService,
              ): GridConfiguration => {
                const config =
                  recordGridViewService.getGridConfiguration(contractTypeId)!;
                config.gridSettings = gridSettingsService.getGridSettings(
                  Symbol(gridEditSymbolKey),
                );
                return config;
              },
            ],
            config: [
              ServerUtilityName,
              "selectedIds",
              "machine",
              "gridConfiguration",
              (
                serverUtility: ServerUtility,
                selectedIds: number[],
                machine: Record,
                gridConfiguration: GridConfiguration,
              ): ConfigurableGridFormViewConfig =>
                getConfigurationGridEditViewConfigForContractMachines({
                  gridConfiguration,
                  currentOnlyToggleButtonSelectionChanged: (
                    button,
                    controller,
                  ) => {
                    const { defaultQuery, selectedIds: ids } =
                      controller.config;
                    let effectiveIds = !ids?.length ? [0] : ids;
                    defaultQuery.ContractInstanceIds = button.checked
                      ? effectiveIds
                      : undefined;
                  },
                  defaultQuery: {
                    ContractInstanceIds:
                      selectedIds.length > 0 ? selectedIds : [0],
                  },
                  selectedIds,
                  gridSelectionChanged: (args: GridSelectionChangedArgs) => {
                    const contractId = <any>args.data.ContractInstanceId;
                    const machineId = machine.Id;
                    setContractMachine(
                      serverUtility,
                      machineId,
                      contractId,
                      args.selected,
                    );
                  },
                  rowIdSelector: (row) => row.ContractInstanceId,
                }),
            ],
          },
        },
      },
    ],
  });
}

export function getContractMachineTab(config: {
  contractTypeId: string;
  gridViewSymbolKey: string;
  gridEditSymbolKey: string;
}): any {
  return {
    name: "Machines",
    uiSref: "machines",
    state: {
      url: "/machines",
      views: {
        tabs: {
          component: configurableGridFormViewComponentName,
        },
      },
      resolve: {
        config: [
          "contract",
          RecordGridViewServiceName,
          GridSettingsServiceName,
          ColumnDefinitionSetServiceName,
          ColumnDefinitionServiceName,
          function (
            contract: Record,
            recordGridViewService: RecordGridViewService,
            gridSettingsService: GridSettingsService,
            columnDefinitionSetService: ColumnDefinitionSetService,
            columnDefinitionService: ColumnDefinitionService,
          ): ConfigurableGridFormViewConfig {
            return getConfigurableGridFormViewConfigForContractMachines(
              recordGridViewService,
              gridSettingsService.getGridSettings(
                Symbol(config.gridViewSymbolKey),
              ),
              { ContractInstanceId: contract.ContractInstanceId },
              columnDefinitionService,
              columnDefinitionSetService,
            );
          },
        ],
      },
    },
    children: [
      {
        name: "Edit",
        uiSref: "edit",
        state: {
          url: "/edit",
          views: {
            "tabs@main.contract.type.info": {
              component: configurableGridFormViewComponentName,
            },
          },
          resolve: {
            selectedIds: [
              RecordGridViewServiceName,
              ServerUtilityName,
              "contract",
              (
                recordGridViewService: RecordGridViewService,
                serverUtility: ServerUtility,
                contract: Record,
              ) => {
                const contractMachineConfig =
                  recordGridViewService.getGridConfiguration(
                    contractMachineName,
                  )!;

                return serverUtility
                  .fetchAll<Record>({
                    url: contractMachineConfig.gridDataSource!.url,
                    query: {
                      ContractInstanceIds: contract.ContractInstanceId,
                    },
                  })
                  .then((result) => result.map((r) => r.MachineId));
              },
            ],
            gridConfiguration: [
              RecordGridViewServiceName,
              GridSettingsServiceName,
              (
                recordGridViewService: RecordGridViewService,
                gridSettingsService: any,
              ) => {
                const gridConfig =
                  recordGridViewService.getGridConfiguration(
                    machineObjectName,
                  )!;
                gridConfig.gridSettings = gridSettingsService.getGridSettings(
                  Symbol(config.gridEditSymbolKey),
                );
                return gridConfig;
              },
            ],
            config: [
              ServerUtilityName,
              "contract",
              "gridConfiguration",
              "selectedIds",
              (
                serverUtility: ServerUtility,
                contract: Record,
                gridConfiguration: GridConfiguration,
                selectedIds: number[],
              ) =>
                getConfigurationGridEditViewConfigForContractMachines({
                  gridConfiguration,
                  selectedIds,
                  gridSelectionChanged(args: GridSelectionChangedArgs) {
                    const ContractInstanceId =
                      contract.ContractInstanceId as number;
                    const machineId: number = <any>args.data.MachineId;
                    setContractMachine(
                      serverUtility,
                      machineId,
                      ContractInstanceId,
                      args.selected,
                    );
                  },
                  rowIdSelector: (row) => row.MachineId,
                  defaultQuery: {
                    MachineIds: selectedIds.length > 0 ? selectedIds : [0],
                  },
                  currentOnlyToggleButtonSelectionChanged: (
                    button,
                    controller,
                  ) => {
                    const { defaultQuery, selectedIds: ids } =
                      controller.config;
                    let effectiveIds = !ids?.length ? [0] : ids;
                    defaultQuery.MachineIds = button.checked
                      ? effectiveIds
                      : undefined;
                  },
                }),
            ],
          },
        },
      },
    ],
  };
}

function setContractMachine(
  serverUtility: ServerUtility,
  machineId: number,
  contractId: number,
  setSelection: boolean,
) {
  const url = "/api/contract/machines";

  setSelection
    ? serverUtility.post({
        url,
        postObject: {
          ContractInstanceId: contractId,
          MachineId: machineId,
        },
      })
    : serverUtility.sendApiRequest<ServiceResponse<Record>>({
        url: url + `/${contractId}/byMachineId`,
        method: "delete",
        data: {
          MachineId: machineId,
        },
      });
}

function getConfigurableGridFormViewConfigForContractMachines(
  recordGridViewService: RecordGridViewService,
  gridSettings: IGridSettings,
  defaultQuery: IQuery,
  columnDefinitionService: ColumnDefinitionService,
  columnDefinitionSetService: ColumnDefinitionSetService,
): ConfigurableGridFormViewConfig {
  const contractMachineGridConfiguration =
    recordGridViewService.getGridConfiguration(contractMachineName)!;

  contractMachineGridConfiguration.nameSingular = "Contract";
  contractMachineGridConfiguration.namePlural = "Contracts";

  contractMachineGridConfiguration.gridSettings = gridSettings;
  const infoColumn: ColDef = columnDefinitionService.getLinkColDef({
    colId: "info",
    headerName: "Info",
    iconClass: "fa fa-info-circle text-primary",
    stateExpression: `main.machines.detail.configurations({machineId: data.MachineId})`,
  });

  contractMachineGridConfiguration.columns = [
    infoColumn,
    ...(columnDefinitionSetService.getColumnDefinitionSet(machineObjectName) ??
      []),
  ];

  return {
    defaultQuery,
    gridConfiguration: contractMachineGridConfiguration,
    additionalToggleButtons: [],
    navButtonIcon: "fa-pencil",
    navButtonPermission: "contractEdit",
    navButtonText: "Edit",
    navPath: ".edit",
    showNavButton: true,
    showSelectButtons: false,
  };
}

function getConfigurationGridEditViewConfigForContractMachines(config: {
  selectedIds: number[];
  gridConfiguration: GridConfiguration;
  gridSelectionChanged: (args: GridSelectionChangedArgs) => void;
  rowIdSelector: (row: any) => number;
  currentOnlyToggleButtonSelectionChanged: (
    button: ToggleButtonDefinition,
    gridController: ConfigurableGridFormViewComponentController,
  ) => void;
  defaultQuery: IQuery;
}): ConfigurableGridFormViewConfig {
  const { selectedIds, gridConfiguration, gridSelectionChanged } = config;

  return {
    defaultQuery: config.defaultQuery,
    gridConfiguration,
    additionalToggleButtons: [
      {
        id: "currentConfigurationTypeConfigurationsOnly",
        text: "Current Only",
        checked: true,
        onChanged: config.currentOnlyToggleButtonSelectionChanged,
      },
    ],
    showNavButton: true,
    navButtonIcon: "fa-pencil",
    navButtonPermission: undefined,
    navButtonText: "Done",
    navPath: "^",
    selectedIds,
    rowIdSelector: config.rowIdSelector,
    showSelectButtons: true,
    gridSelectionChanged,
  };
}
